/**
 * The countries money may not come from or go to (0081), and who may change that.
 *
 * The list mirrors the banking partner's own: a weekly job ADDS what they add,
 * and never removes. When they stop listing a country it becomes "no longer
 * listed by the partner" and stays blocked until a super_admin lifts it by hand
 * with a reason — a parser must never be what re-opens a sanctioned country.
 *
 * A super_admin can also block a country as QMI's own decision; the weekly job never
 * touches those rows.
 *
 * This screen is global and super_admin-gated, which is why it is not on the
 * per-client risk page.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import authService from '../../services/auth.service';
import clientRiskService, {
  type BlockedCountriesAdmin,
  type BlockedCountry,
} from '../../services/clientRisk.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { countryLocale, countryName, countryOptions, readerCountryName } from '../../lib/countries';
import { activeLanguage } from '../../i18n/language';
import { colors } from '../../styles/theme';
import {
  addCountryReady,
  classifyCountry,
  cleanSourceUrl,
  describeSyncRun,
  isQmiCountry,
} from '../staff/clientRisk.helpers';

const STATE_TONE: Record<'blocked' | 'removal_pending' | 'lifted', BadgeTone> = {
  blocked: 'danger',
  removal_pending: 'warning',
  lifted: 'neutral',
};

const STATE_LABEL: Record<'blocked' | 'removal_pending' | 'lifted', string> = {
  blocked: 'Blocked',
  removal_pending: 'Partner no longer lists it',
  lifted: 'Lifted',
};

export default function BlockedCountries(): JSX.Element {
  const [data, setData] = useState<BlockedCountriesAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const isSuperAdmin = authService.getRole() === 'super_admin';

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await clientRiskService.blockedCountries());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the country list.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const lastRun = data?.recent_runs[0];

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Blocked countries"
        subtitle="Whatever our banking partner blocks, we block: money in, money out and addresses."
      />

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </div>
      )}

      {lastRun && (
        <section className="rounded-2xl p-5 mb-6"
                 style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
          <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Last check</h2>
          <p className="text-sm" style={{ color: colors.muted }}>
            {formatDateTime(lastRun.ran_at)} — {describeSyncRun(lastRun)}
          </p>
          <p className="text-xs mt-2" style={{ color: colors.muted }}>
            The job only ever adds. Removing a country is a decision, taken here.
          </p>
        </section>
      )}

      {isSuperAdmin && (
        <AddCountry
          onDone={(msg) => { setFlash(msg); void reload(); }}
          onError={setError}
        />
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {data && (
        <section className="rounded-2xl p-5"
                 style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: colors.muted }}>
                <th className="text-left font-medium py-2">Country</th>
                <th className="text-left font-medium py-2">State</th>
                <th className="text-left font-medium py-2">Last confirmed</th>
                <th className="text-left font-medium py-2" />
              </tr>
            </thead>
            <tbody>
              {data.countries.map((c) => (
                <CountryRow
                  key={c.country_code}
                  country={c}
                  isSuperAdmin={isSuperAdmin}
                  onDone={(msg) => { setFlash(msg); void reload(); }}
                  onError={setError}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function CountryRow({ country, isSuperAdmin, onDone, onError }: Readonly<{
  country: BlockedCountry;
  isSuperAdmin: boolean;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const row = classifyCountry(country);
  // The name in the reader's language; the server's English name only for a code the browser does not know.
  const name = readerCountryName(country.country_code) || country.country_name;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = reason.trim().length >= 20 && code.trim().length >= 6 && !busy;

  const lift = async (): Promise<void> => {
    setBusy(true);
    try {
      await clientRiskService.liftCountry(country.country_code, reason.trim(), code.trim());
      setOpen(false);
      setReason('');
      setCode('');
      onDone(`${name} is no longer blocked.`);
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not lift the block.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr style={{ borderTop: `1px solid ${colors.border}` }}>
        <td className="py-2" style={{ color: colors.ink }}>
          {name} <span style={{ color: colors.muted }}>({country.country_code})</span>
        </td>
        <td className="py-2">
          <StatusBadge tone={STATE_TONE[row.state]}>{STATE_LABEL[row.state]}</StatusBadge>
          {isQmiCountry(country) && (
            <span className="ml-2 text-xs" style={{ color: colors.muted }}>Decided by QMI</span>
          )}
        </td>
        <td className="py-2" style={{ color: colors.muted }}>
          {row.verified ? formatDateTime(row.verified) : '—'}
        </td>
        <td className="py-2 text-right">
          {row.state !== 'lifted' && isSuperAdmin && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-sm font-semibold"
              style={{ color: colors.navy }}
            >
              {open ? 'Cancel' : 'Unblock'}
            </button>
          )}
          {row.state !== 'lifted' && !isSuperAdmin && (
            <span className="text-xs" style={{ color: colors.muted }}>
              Only a super administrator can unblock a country.
            </span>
          )}
          {row.state === 'lifted' && country.lift_reason && (
            <span className="text-xs" style={{ color: colors.muted }}>{country.lift_reason}</span>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} className="pb-4">
            <div className="rounded-xl p-4" style={{ border: `1px solid ${colors.border}` }}>
              <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
                Read the partner’s own page before you do this. Nothing else re-opens a country:
                the weekly job adds and never removes, precisely so this is a decision someone
                signs their name to.
              </p>
              <label htmlFor={`lift-${country.country_code}`} className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
                Why is it safe to unblock {name}?
              </label>
              <textarea
                id={`lift-${country.country_code}`}
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg text-sm mb-3"
                style={{ border: `1px solid ${colors.border}` }}
              />
              <label htmlFor={`lift-mfa-${country.country_code}`} className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
                Authentication code
              </label>
              <input
                id={`lift-mfa-${country.country_code}`}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="6-digit code"
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono tracking-widest mb-3"
                style={{ border: `1px solid ${colors.border}` }}
              />
              <button
                type="button"
                disabled={!ready}
                onClick={() => { void lift(); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                style={{ background: colors.navy, color: '#FFFFFF' }}
              >
                {busy ? 'Unblocking…' : <>Unblock {name}</>}
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AddCountry({ onDone, onError }: Readonly<{
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const options = countryOptions(countryLocale(activeLanguage()));
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [link, setLink] = useState('');
  const [mfa, setMfa] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = addCountryReady({ code, reason, mfaCode: mfa, busy });

  const block = async (): Promise<void> => {
    setBusy(true);
    try {
      await clientRiskService.addCountry({
        country_code: code,
        country_name: countryName(code, 'en') ?? code,
        reason: reason.trim(),
        source_url: cleanSourceUrl(link),
        mfa_code: mfa.trim(),
      });
      const shown = readerCountryName(code) || code;
      setCode('');
      setReason('');
      setLink('');
      setMfa('');
      onDone(`${shown} is now blocked.`);
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not block the country.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl p-5 mb-6"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Block a country</h2>
      <p className="text-sm mb-3" style={{ color: colors.muted }}>
        This is a decision of QMI. It applies to residence, bank accounts and payout
        destinations, like the list of the partner, and the weekly job never changes it.
      </p>
      <label htmlFor="block-country" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        Country
      </label>
      <select
        id="block-country"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        disabled={busy}
        className="w-full px-3 py-2 rounded-lg text-sm mb-3"
        style={{ border: `1px solid ${colors.border}` }}
      >
        <option value="">Choose a country</option>
        {options.map((o) => (
          <option key={o.code} value={o.code}>{o.name}</option>
        ))}
      </select>
      <label htmlFor="block-reason" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        Why is it blocked?
      </label>
      <textarea
        id="block-reason"
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value.slice(0, 500))}
        disabled={busy}
        className="w-full px-3 py-2 rounded-lg text-sm mb-3"
        style={{ border: `1px solid ${colors.border}` }}
      />
      <label htmlFor="block-link" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        Source link (optional)
      </label>
      <input
        id="block-link"
        type="url"
        value={link}
        onChange={(e) => setLink(e.target.value.slice(0, 500))}
        disabled={busy}
        className="w-full px-3 py-2 rounded-lg text-sm mb-3"
        style={{ border: `1px solid ${colors.border}` }}
      />
      <label htmlFor="block-mfa" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        Authentication code
      </label>
      <input
        id="block-mfa"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={mfa}
        onChange={(e) => setMfa(e.target.value.replace(/\D/g, '').slice(0, 8))}
        placeholder="6-digit code"
        disabled={busy}
        className="w-full px-3 py-2 rounded-lg text-sm font-mono tracking-widest mb-3"
        style={{ border: `1px solid ${colors.border}` }}
      />
      <button
        type="button"
        disabled={!ready}
        onClick={() => { void block(); }}
        className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
        style={{ background: colors.navy, color: '#FFFFFF' }}
      >
        {busy ? 'Blocking…' : 'Block country'}
      </button>
    </section>
  );
}
